import React, { useState, useEffect } from 'react';
import { historialAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
    History, 
    Search, 
    FileText, 
    CheckCircle, 
    XCircle, 
    Truck, 
    Clock,
    RefreshCw,
    User,
} from 'lucide-react';

export default function HistorialPage() {
    const [historial, setHistorial] = useState([]);
    const [filteredHistorial, setFilteredHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadHistorial();
    }, []);

    useEffect(() => {
        filterHistorial();
    }, [historial, searchTerm]);

    const loadHistorial = async () => {
        setLoading(true);
        try {
            const response = await historialAPI.getAll();
            setHistorial(response.data);
        } catch (err) {
            console.error('Error loading historial:', err);
        } finally {
            setLoading(false);
        }
    };

    const filterHistorial = () => {
        if (!searchTerm) {
            setFilteredHistorial(historial);
            return;
        }

        const term = searchTerm.toLowerCase();
        setFilteredHistorial(
            historial.filter(
                (item) =>
                    item.folio.toLowerCase().includes(term) ||
                    item.accion.toLowerCase().includes(term) ||
                    item.usuario_nombre.toLowerCase().includes(term)
            )
        );
    };

    const getActionIcon = (accion) => {
        if (accion.includes('CREACIÓN')) return <FileText className="h-5 w-5 text-primary" />;
        if (accion.includes('AUTORIZADO')) return <CheckCircle className="h-5 w-5 text-success" />;
        if (accion.includes('RECHAZADO') || accion.includes('rechazado')) return <XCircle className="h-5 w-5 text-destructive" />;
        if (accion.includes('DESPACHADO')) return <Truck className="h-5 w-5 text-success" />;
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    };

    const getActionColor = (accion) => {
        if (accion.includes('AUTORIZADO') || accion.includes('DESPACHADO')) return 'border-l-success';
        if (accion.includes('RECHAZADO') || accion.includes('rechazado')) return 'border-l-destructive';
        return 'border-l-primary';
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Historial
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Registro de todas las acciones en el sistema
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadHistorial}
                        className="border-2"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>

                {/* Search */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por folio, acción o usuario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 input-industrial"
                                data-testid="search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* History List */}
                <Card className="border-2">
                    <CardHeader className="card-industrial-header">
                        <CardTitle className="flex items-center gap-2">
                            <History className="h-5 w-5" />
                            Registros ({filteredHistorial.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-4 space-y-4">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Skeleton key={i} className="h-20" />
                                ))}
                            </div>
                        ) : filteredHistorial.length === 0 ? (
                            <div className="empty-state py-12">
                                <History className="empty-state-icon" />
                                <p className="empty-state-title">Sin registros</p>
                                <p className="empty-state-description">
                                    {searchTerm
                                        ? 'No se encontraron registros con los filtros aplicados'
                                        : 'El historial aparecerá aquí cuando se realicen acciones'}
                                </p>
                            </div>
                        ) : (
                            <ScrollArea className="max-h-[600px]">
                                <div className="divide-y divide-border">
                                    {filteredHistorial.map((item) => (
                                        <div
                                            key={item.id}
                                            className={`p-4 border-l-4 ${getActionColor(item.accion)} hover:bg-muted/30 transition-colors`}
                                            data-testid={`history-item-${item.id}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="p-2 rounded-sm bg-muted/50">
                                                    {getActionIcon(item.accion)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-4">
                                                        <p className="font-medium">{item.accion}</p>
                                                        <span className="folio-display text-xs shrink-0">{item.folio}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <User className="h-3 w-3" />
                                                            {item.usuario_nombre}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="h-3 w-3" />
                                                            {formatDate(item.created_at)}
                                                        </span>
                                                    </div>
                                                    {item.detalles && (
                                                        <div className="mt-2 p-2 bg-muted/30 rounded-sm text-sm">
                                                            {item.detalles.observacion && (
                                                                <p className="italic">"{item.detalles.observacion}"</p>
                                                            )}
                                                            {item.detalles.motivos_rechazo && item.detalles.motivos_rechazo.length > 0 && (
                                                                <p className="text-destructive">
                                                                    Motivos: {item.detalles.motivos_rechazo.join(', ')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
